// src/components/landing/LoginForm.tsx
// REDESIGNED - Matches ContactCTA & ProfileJourneyModal structure EXACTLY
"use client";

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, LogIn, Sparkles } from 'lucide-react';
import { useAuth } from "@/contexts/AuthContext";
import CONFIG from "@/lib/config";
import { InfoBox } from '@/components/custom-ui/info-box';
import { getUserApi } from '@/lib/api/client';
import { friendlyAuthError } from '@/lib/api/authErrors';

interface LoginFormProps {
    /**
     * Called once the auth API resolves. `user` is populated on success and
     * lets the caller role-gate before navigating / toasting.
     */
    onLoginCallback?: (success: boolean, user?: { role?: string }) => void;
    onRegisterClick?: () => void;
    mode?: 'standalone' | 'embedded';
    /**
     * Hides the Google login button + divider. Used on /admin to avoid
     * letting OAuth bypass the admin-only role check (Google's redirect flow
     * doesn't pass through this component's success handler).
     */
    disableSocialLogin?: boolean;
    /** Override the standalone-mode header title. */
    title?: string;
    /** Override the standalone-mode header subtitle. */
    subtitle?: string;
    /**
     * Seeds the email field — set when we already know the address, e.g. the
     * user just signed up with it. Saves retyping something typed seconds ago.
     *
     * Only the EMAIL. Never the password: the address is not a secret, the
     * password is.
     */
    prefillEmail?: string;
}

export const LoginForm = ({
                              onLoginCallback,
                              onRegisterClick,
                              mode = 'standalone',
                              disableSocialLogin = false,
                              title = 'Logga in till Nexilink',
                              subtitle = 'Välkommen tillbaka!',
                              prefillEmail,
                          }: LoginFormProps) => {
    const { login, initiateGoogleLogin } = useAuth();

    // Seeded in the initialiser rather than a useEffect: an effect would land
    // after first paint and could overwrite something the browser's password
    // manager had already filled, leaving the manager's password paired with
    // our email. Set once, then the field belongs to the user.
    const [loginData, setLoginData] = useState({
        email: prefillEmail ?? '',
        password: '',
    });

    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    /**
     * The login was refused because the address is unconfirmed. Without a way to
     * ask for a new link right here, anyone who lost the original mail is simply
     * locked out — so the refusal and its remedy live in the same box.
     */
    const [needsConfirmation, setNeedsConfirmation] = useState(false);
    const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent'>('idle');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setLoginData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        setIsSubmitting(true);
        setError('');
        setNeedsConfirmation(false);
        setResendState('idle');

        try {
            const { success, error: loginError, user } = await login(loginData.email, loginData.password);

            if (!success && loginError) {
                // Copy comes from the shared map, keyed on the API's error CODE.
                // The old branch here read only statusCode, so every 403 said
                // "verifiera din e-postadress" — including the ones that mean
                // password login is disabled, or Google login is off.
                const friendly = friendlyAuthError(loginError);
                setError(`${friendly.title}. ${friendly.description}`);
                setNeedsConfirmation(Boolean(friendly.needsEmailConfirmation));
            }

            if (onLoginCallback) {
                onLoginCallback(success, user);
            }
        } catch (error) {
            console.error('Login error:', error);
            setError('Ett oväntat fel uppstod. Försök igen.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResendConfirmation = async () => {
        setResendState('sending');
        try {
            await getUserApi().resendVerificationEmail(loginData.email.trim());
        } catch {
            // The API answers 200 whether or not the address exists, so a throw
            // here is a transport failure, not "no such account". Either way the
            // honest thing to show is the same neutral line.
        }
        setResendState('sent');
    };

    // Google Login - uses config path
    const handleGoogleLogin = async () => {
        setIsSubmitting(true);
        setError('');

        try {
            // Use config path for OAuth callback
            const { success, googleUrl, error: googleError } = await initiateGoogleLogin(CONFIG.GOOGLE_OAUTH_CALLBACK_PATH);

            if (success && googleUrl) {
                // Redirect to Google OAuth URL
                window.location.href = googleUrl;
            } else if (googleError) {
                setError('Ett fel uppstod vid Google-inloggning. Försök igen.');
            }
        } catch (error) {
            console.error('Google login error:', error);
            setError('Ett fel uppstod vid Google-inloggning. Försök igen.');
        } finally {
            // Don't set loading to false here as we're redirecting
            // setIsSubmitting(false);
        }
    };

    // Google login button
    const googleButton = (
        <button
            type="button"
            onClick={handleGoogleLogin}
            disabled={isSubmitting}
            className="w-full flex justify-center items-center gap-2 px-4 py-2.5 text-sm text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
            <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
                <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z" />
                <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
                <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z" />
                <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
            </svg>
            {isSubmitting ? 'Omdirigerar...' : 'Fortsätt med Google'}
        </button>
    );

    const loginButton = (
        <button
            type="submit"
            disabled={isSubmitting || !loginData.email || !loginData.password}
            className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-[#1A4D4F] text-[#f5f5f5] font-semibold rounded-xl hover:bg-[#1A4D4F]/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
        >
            {isSubmitting ? (
                <>
                    <div className="w-4 h-4 border-2 border-[#f5f5f5] border-t-transparent rounded-full animate-spin" />
                    Loggar in...
                </>
            ) : (
                <>
                    <LogIn className="w-4 h-4" />
                    Logga in
                </>
            )}
        </button>
    );

    // Embedded mode - lean layout for slide-over panels
    if (mode === 'embedded') {
        return (
            <form onSubmit={handleSubmit} className="flex flex-col h-full">
                <div className="flex-1 overflow-y-auto px-6 py-5">
                    {error && (
                        <InfoBox variant="error" className="mb-4">
                            {error}
                            {needsConfirmation && (
                                <div className="mt-2">
                                    {resendState === 'sent' ? (
                                        <span className="text-xs font-medium">
                                            Har du inte fått mejlet? Kolla skräpposten — vi har skickat en ny länk.
                                        </span>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={handleResendConfirmation}
                                            disabled={resendState === 'sending' || !loginData.email}
                                            className="text-sm font-semibold text-[#1A4D4F] hover:underline disabled:opacity-50"
                                        >
                                            {resendState === 'sending' ? 'Skickar...' : 'Skicka en ny bekräftelselänk →'}
                                        </button>
                                    )}
                                </div>
                            )}
                        </InfoBox>
                    )}
                    <div className="space-y-4">
                        <div>
                            <label className="block text-xs font-semibold mb-1.5">E-post *</label>
                            <input
                                type="email"
                                name="email"
                                value={loginData.email}
                                onChange={handleChange}
                                required
                                disabled={isSubmitting}
                                className="w-full px-3 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1A4D4F] focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed transition-all"
                                placeholder="din.email@exempel.se"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-semibold mb-1.5">Lösenord *</label>
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    value={loginData.password}
                                    onChange={handleChange}
                                    required
                                    disabled={isSubmitting}
                                    className="w-full px-3 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1A4D4F] focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed transition-all pr-10"
                                    placeholder="Ditt lösenord"
                                />
                                <button
                                    type="button"
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 disabled:opacity-50 transition-colors"
                                    onClick={() => setShowPassword(!showPassword)}
                                    disabled={isSubmitting}
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>

                        <div className="flex justify-end">
                            <Link
                                to="/forgot-password"
                                className="text-xs text-[#1A4D4F] hover:text-[#1A4D4F]/80 font-medium hover:underline transition-colors"
                            >
                                Glömt lösenord?
                            </Link>
                        </div>

                        {loginButton}

                        {!disableSocialLogin && (
                            <>
                                <div className="flex items-center">
                                    <div className="flex-grow border-t-2 border-gray-200"></div>
                                    <span className="mx-3 text-xs text-gray-500 font-medium">eller</span>
                                    <div className="flex-grow border-t-2 border-gray-200"></div>
                                </div>

                                {googleButton}
                            </>
                        )}

                        {onRegisterClick && (
                            <div className="text-center pt-1">
                                <span className="text-xs text-gray-600">Har du inget konto? </span>
                                <button
                                    type="button"
                                    onClick={onRegisterClick}
                                    disabled={isSubmitting}
                                    className="text-xs text-[#1A4D4F] hover:text-[#1A4D4F]/80 font-semibold hover:underline disabled:opacity-50 transition-colors"
                                >
                                    Registrera dig här
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </form>
        );
    }

    // Standalone mode - full card with header
    return (
        <div className="w-full max-w-2xl mx-auto h-full flex items-center justify-center p-6 sm:p-8">
            <div className="w-full max-h-[85vh] bg-white rounded-2xl border-2 border-gray-100 flex flex-col overflow-hidden">
                <div className="flex-shrink-0 p-5">
                    <div className="text-center">
                        <div className="w-12 h-12 mx-auto mb-2 bg-[#A4C3B2] rounded-2xl flex items-center justify-center">
                            <LogIn className="w-6 h-6 text-[#1A4D4F]" />
                        </div>
                        <h2 className="text-lg font-bold mb-1 text-[#212121]">{title}</h2>
                        <p className="text-sm text-gray-600">{subtitle}</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 pb-4">
                    {error && (
                        <InfoBox variant="error" className="mb-4">
                            {error}
                            {needsConfirmation && (
                                <div className="mt-2">
                                    {resendState === 'sent' ? (
                                        <span className="text-xs font-medium">
                                            Har du inte fått mejlet? Kolla skräpposten — vi har skickat en ny länk.
                                        </span>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={handleResendConfirmation}
                                            disabled={resendState === 'sending' || !loginData.email}
                                            className="text-sm font-semibold text-[#1A4D4F] hover:underline disabled:opacity-50"
                                        >
                                            {resendState === 'sending' ? 'Skickar...' : 'Skicka en ny bekräftelselänk →'}
                                        </button>
                                    )}
                                </div>
                            )}
                        </InfoBox>
                    )}
                    <div className="space-y-4">
                        <div>
                            <label className="block text-xs font-semibold mb-1.5">E-post *</label>
                            <input
                                type="email"
                                name="email"
                                value={loginData.email}
                                onChange={handleChange}
                                required
                                disabled={isSubmitting}
                                className="w-full px-3 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1A4D4F] focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed transition-all"
                                placeholder="din.email@exempel.se"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-semibold mb-1.5">Lösenord *</label>
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    value={loginData.password}
                                    onChange={handleChange}
                                    required
                                    disabled={isSubmitting}
                                    className="w-full px-3 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1A4D4F] focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed transition-all pr-10"
                                    placeholder="Ditt lösenord"
                                />
                                <button
                                    type="button"
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 disabled:opacity-50 transition-colors"
                                    onClick={() => setShowPassword(!showPassword)}
                                    disabled={isSubmitting}
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>

                        <div className="flex justify-end">
                            <Link
                                to="/forgot-password"
                                className="text-xs text-[#1A4D4F] hover:text-[#1A4D4F]/80 font-medium hover:underline transition-colors"
                            >
                                Glömt lösenord?
                            </Link>
                        </div>

                        {loginButton}

                        {!disableSocialLogin && (
                            <>
                                <div className="flex items-center">
                                    <div className="flex-grow border-t-2 border-gray-200"></div>
                                    <span className="mx-3 text-xs text-gray-500 font-medium">eller</span>
                                    <div className="flex-grow border-t-2 border-gray-200"></div>
                                </div>

                                {googleButton}
                            </>
                        )}

                        {onRegisterClick && (
                            <div className="text-center pt-1">
                                <span className="text-xs text-gray-600">Har du inget konto? </span>
                                <button
                                    type="button"
                                    onClick={onRegisterClick}
                                    disabled={isSubmitting}
                                    className="text-xs text-[#1A4D4F] hover:text-[#1A4D4F]/80 font-semibold hover:underline disabled:opacity-50 transition-colors"
                                >
                                    Registrera dig här
                                </button>
                            </div>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};